import React, { useState, useEffect, useRef, useCallback } from 'react';
import { z } from 'zod';
import { getSchemaDescription } from '../../lib/utils';
import { ScrollText, AlertTriangle } from 'lucide-react';

// Define type for field metadata
interface FieldMetadata {
  description?: string;
  attributes: Array<{
    name: string;
    value?: string;
    category: 'type' | 'constraint';
  }>;
}

// Define the component props interface
interface PathInputWithAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  sourceZod?: z.ZodTypeAny;
}

// Add this new component for path input with autocomplete
export const PathInputWithAutocomplete: React.FC<PathInputWithAutocompleteProps> = ({ 
  value, 
  onChange, 
  sourceZod 
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [fieldMetadata, setFieldMetadata] = useState<Record<string, FieldMetadata>>({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Cache for all paths, only compute once for the same schema
  const allPathsCache = useRef<string[]>([]);
  // Cache for field metadata to avoid repeated lookups
  const metadataCache = useRef<Record<string, FieldMetadata>>({});
  
  // Check if suggestions are available
  const areSuggestionsAvailable = !!sourceZod;
  
  // Simplified function to extract schema attributes using dynamic approach
  const extractSchemaAttributes = useCallback((schema: z.ZodTypeAny): FieldMetadata['attributes'] => {
    const attributes: FieldMetadata['attributes'] = [];
    
    // Don't process if schema is missing or invalid
    if (!schema || !schema._def) {
      return attributes;
    }
    
    // Maps for wrapper types - these modify a type but aren't the base type
    const WRAPPER_TYPES: Record<string, string> = {
      'ZodOptional': 'optional',
      'ZodNullable': 'nullable',
      'ZodDefault': 'default'
    };
    
    // Map of special checks that should replace the base type
    // Only include entries that actually transform the type display
    const SPECIAL_TYPE_CHECKS: Record<string, string> = {
      'email': 'email',
      'url': 'url',
      'uuid': 'uuid',
      'int': 'integer'
    };
    
    try {
      // Extract base type (unwrap if needed)
      let currentSchema = schema;
      
      // Track which wrappers we've found (using a Set for uniqueness)
      const foundWrappers = new Set<string>();
      
      // Unwrap until we get to a non-wrapper type
      while (currentSchema && currentSchema._def) {
        const typeName = currentSchema._def.typeName;
        
        // Check if this is a wrapper type
        if (WRAPPER_TYPES[typeName]) {
          // Add this wrapper to our found wrappers
          foundWrappers.add(WRAPPER_TYPES[typeName]);
          
          // Unwrap to the inner type
          currentSchema = currentSchema._def.innerType || null;
        } else {
          // Not a wrapper type
          break;
        }
        
        // Break the loop if we've reached the end
        if (!currentSchema) break;
      }
      
      // Now currentSchema should be the unwrapped type
      if (currentSchema && currentSchema._def) {
        // Get the base type by removing 'Zod' prefix
        let baseType = currentSchema._def.typeName.replace(/^Zod/, '').toLowerCase();
        
        // Check for special types that replace the base type (email, url, etc.)
        const checks = currentSchema._def.checks;
        if (checks && Array.isArray(checks)) {
          for (const check of checks) {
            if (check.kind && SPECIAL_TYPE_CHECKS[check.kind]) {
              baseType = SPECIAL_TYPE_CHECKS[check.kind];
              break;
            }
          }
        }
        
        // Add the base type
        attributes.push({ name: baseType, category: 'type' });
        
        // Add constraints from the wrappers we found
        foundWrappers.forEach(wrapper => {
          attributes.push({ name: wrapper, category: 'constraint' });
        });
      }
    } catch (error) {
      console.error('Error extracting schema attributes:', error);
    }
    
    return attributes;
  }, []);

  // Get schema directly for a specific path
  const getSchemaForPath = useCallback((rootSchema: z.ZodTypeAny | undefined, path: string): z.ZodTypeAny | undefined => {
    if (!rootSchema || !path) {
      return undefined;
    }
    
    try {
      // Split the path into segments
      const segments = path.split('.');
      let currentSchema: z.ZodTypeAny = rootSchema;
      
      // Traverse the path segments to find the target schema
      for (const segment of segments) {
        if (!(currentSchema instanceof z.ZodObject)) {
          // Try to unwrap wrapper types
          if ('innerType' in currentSchema._def && currentSchema._def.innerType) {
            currentSchema = currentSchema._def.innerType;
            // Retry with this segment
            if (currentSchema instanceof z.ZodObject) {
              currentSchema = currentSchema.shape[segment];
            } else {
              return undefined;
            }
          } else {
            return undefined;
          }
        } else {
          // Get the schema for this segment
          currentSchema = currentSchema.shape[segment];
          if (!currentSchema) {
            return undefined;
          }
        }
      }
      
      return currentSchema;
    } catch (error) {
      console.error(`Error getting schema for path ${path}:`, error);
      return undefined;
    }
  }, []);

  // Generate all possible paths from the Zod schema (memoized)
  const getAllPaths = useCallback(() => {
    // Return cached paths if available
    if (allPathsCache.current.length > 0) {
      return allPathsCache.current;
    }
    
    // If no Zod schema is provided, return empty array
    if (!sourceZod) {
      return [];
    }
    
    // Extract paths from Zod schema
    const extractPathsFromZod = (schema: z.ZodTypeAny, prefix = ''): string[] => {
      // Handle non-object types
      if (!(schema instanceof z.ZodObject)) {
        // Handle ZodEffects and other wrapper types that might contain an inner schema
        if ('innerType' in schema._def && schema._def.innerType) {
          return extractPathsFromZod(schema._def.innerType, prefix);
        }
        return prefix ? [prefix] : [];
      }

      // Handle object types
      const { shape } = schema;
      if (!shape) return prefix ? [prefix] : [];

      return Object.entries(shape).flatMap(([key, fieldSchema]) => {
        const currentPath = prefix ? `${prefix}.${key}` : key;
        
        // Store metadata for this field
        if (currentPath && fieldSchema) {
          const description = getSchemaDescription(sourceZod, currentPath);
          const attributes = extractSchemaAttributes(fieldSchema as z.ZodTypeAny);
          
          metadataCache.current[currentPath] = {
            description: description || '',
            attributes
          };
        }
        
        // For object types, recursively get nested paths
        if (fieldSchema instanceof z.ZodObject) {
          return [currentPath, ...extractPathsFromZod(fieldSchema, currentPath)];
        }
        
        // For optional fields, check the inner type
        if (fieldSchema instanceof z.ZodOptional) {
          if (fieldSchema._def.innerType instanceof z.ZodObject) {
            return [currentPath, ...extractPathsFromZod(fieldSchema._def.innerType, currentPath)];
          }
        }
        
        // For other field types, just return the current path
        return [currentPath];
      });
    };
    
    // Generate paths from Zod schema
    const paths = extractPathsFromZod(sourceZod);
    
    // Cache the results
    allPathsCache.current = paths;
    return paths;
  }, [sourceZod, extractSchemaAttributes]);

  // Update the getFieldMetadataForPath function:
  const getFieldMetadataForPath = useCallback((path: string): FieldMetadata => {
    // Check cache first
    if (metadataCache.current[path] !== undefined) {
      return metadataCache.current[path];
    }
    
    if (!sourceZod) {
      const emptyMetadata: FieldMetadata = {
        attributes: []
      };
      metadataCache.current[path] = emptyMetadata;
      return emptyMetadata;
    }
    
    // Get schema directly for this path
    const fieldSchema = getSchemaForPath(sourceZod, path);
    
    if (!fieldSchema) {
      const defaultMetadata: FieldMetadata = {
        attributes: []
      };
      metadataCache.current[path] = defaultMetadata;
      return defaultMetadata;
    }
    
    // Extract description and attributes
    const description = getSchemaDescription(sourceZod, path);
    const attributes = extractSchemaAttributes(fieldSchema);
    
    // Cache and return the metadata
    const metadata: FieldMetadata = {
      description: description || '',
      attributes
    };
    
    metadataCache.current[path] = metadata;
    return metadata;
  }, [sourceZod, extractSchemaAttributes, getSchemaForPath]);

  // Only fetch metadata for visible suggestions (lazy loading)
  useEffect(() => {
    if (showSuggestions && suggestions.length > 0) {
      const newMetadata: Record<string, FieldMetadata> = {};
      
      // Only process visible suggestions
      suggestions.forEach(path => {
        newMetadata[path] = getFieldMetadataForPath(path);
      });
      
      setFieldMetadata(newMetadata);
    }
  }, [showSuggestions, suggestions, getFieldMetadataForPath]);

  // Filter suggestions based on current input (debounced)
  const filterSuggestions = useCallback(
    (input: string) => {
      if (!input.trim() || !areSuggestionsAvailable) {
        return [];
      }

      const allPaths = getAllPaths();
      return allPaths
        .filter((path) => path.toLowerCase().includes(input.toLowerCase()))
        .sort()
        .slice(0, 50); // Limit to 50 results for better performance
    },
    [getAllPaths, areSuggestionsAvailable],
  );

  // Create debounced input handler
  const debouncedHandleInput = useCallback((inputValue: string) => {
    const filteredSuggestions = filterSuggestions(inputValue);
    setSuggestions(filteredSuggestions);
    setShowSuggestions(filteredSuggestions.length > 0);
  }, [filterSuggestions]);
  
  // Use useRef to hold the timeout ID
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle input changes with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Don't show suggestions if no Zod schema is provided
    if (!areSuggestionsAvailable) return;
    
    // Clear previous timeout if exists
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timeout
    debounceTimerRef.current = setTimeout(() => {
      debouncedHandleInput(newValue);
    }, 150);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  // Handle keyboard navigation for suggestions
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    const suggestionItems =
      suggestionsRef.current?.querySelectorAll('.suggestion-item');
    const currentIndex = Array.from(suggestionItems || []).findIndex((item) =>
      item.classList.contains('highlighted'),
    );

    let highlighted;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (suggestionItems) {
          const nextIndex =
            currentIndex < suggestionItems.length - 1 ? currentIndex + 1 : 0;
          suggestionItems.forEach((item, i) => {
            item.classList.toggle('highlighted', i === nextIndex);
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (suggestionItems) {
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : suggestionItems.length - 1;
          suggestionItems.forEach((item, i) => {
            item.classList.toggle('highlighted', i === prevIndex);
          });
        }
        break;
      case 'Enter':
        e.preventDefault();
        highlighted = suggestionsRef.current?.querySelector('.highlighted');
        if (highlighted) {
          const suggestionText = highlighted.getAttribute('data-path') || '';
          handleSelectSuggestion(suggestionText);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Clear caches when component unmounts or when sourceZod changes
  useEffect(() => {
    allPathsCache.current = [];
    metadataCache.current = {};
    
    return () => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [sourceZod]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show suggestions on focus
  const handleFocus = () => {
    if (value && areSuggestionsAvailable) {
      const filteredSuggestions = filterSuggestions(value);
      setSuggestions(filteredSuggestions);
      setShowSuggestions(filteredSuggestions.length > 0);
    }
  };

  // Render attribute tag
  const renderAttributeTag = (attribute: FieldMetadata['attributes'][0], index: number) => {
    const className = `attribute-tag ${attribute.category}-tag`;
    return (
      <span key={index} className={className}>
        {attribute.name}{attribute.value ? `: ${attribute.value}` : ''}
      </span>
    );
  };

  // Modify the getUniqueAttributes function to preserve type tags while deduplicating constraints
  const getUniqueAttributes = (attributes: FieldMetadata['attributes']): FieldMetadata['attributes'] => {
    // Extract type attributes (should typically be just one)
    const typeAttributes = attributes.filter(attr => attr.category === 'type');
    
    // Process constraint attributes to deduplicate
    const uniqueConstraints = new Map<string, FieldMetadata['attributes'][0]>();
    
    // Process constraints in reverse to prioritize child attributes over parent ones
    [...attributes]
      .filter(attr => attr.category === 'constraint')
      .reverse()
      .forEach(attr => {
        // Only add if not already present
        if (!uniqueConstraints.has(attr.name)) {
          uniqueConstraints.set(attr.name, attr);
        }
      });
    
    // Combine type attributes and unique constraints
    // Put type first, then constraints
    return [
      ...typeAttributes,
      ...Array.from(uniqueConstraints.values()).reverse()
    ];
  };

  return (
    <div className="path-input-container">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={() => {
          // Delayed hiding to allow clicking on suggestions
          setTimeout(() => {
            if (!inputRef.current?.contains(document.activeElement)) {
              setShowSuggestions(false);
            }
          }, 200);
        }}
        onKeyDown={handleKeyDown}
        className="path-input"
        placeholder="Enter data path (e.g. user.profile.name)"
      />

      {showSuggestions && (
        <div ref={suggestionsRef} className="path-suggestions">
          {suggestions.length === 0 ? (
            <div className="suggestion-item no-results">
              No matching paths found
            </div>
          ) : (
            suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`suggestion-item ${
                  index === 0 ? 'highlighted' : ''
                }`}
                data-path={suggestion}
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <div className="suggestion-path">{suggestion}</div>
                {fieldMetadata[suggestion] && fieldMetadata[suggestion].attributes.length > 0 && (
                  <div className="suggestion-metadata">
                    {getUniqueAttributes(fieldMetadata[suggestion].attributes).map((attr, i) => 
                      renderAttributeTag(attr, i)
                    )}
                  </div>
                )}
                {fieldMetadata[suggestion]?.description ? (
                  <div className="suggestion-description">
                    <span className="description-icon"><ScrollText className="h-4 w-4" /></span> {fieldMetadata[suggestion].description}
                  </div>
                ) : (
                  <div className="suggestion-description missing-description">
                    <span className="description-icon"><AlertTriangle className="h-4 w-4 text-amber-400" /></span> No description available
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <style>{`
        .path-input-container {
          position: relative;
          width: 100%;
          min-width: 0; /* Prevent overflow */
        }
        
        .path-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border-color, #4a5568);
          border-radius: 4px;
          font-size: 14px;
          background-color: var(--bg-light, #2d3748);
          color: var(--text-dark, #e2e8f0);
          box-sizing: border-box; /* Ensure padding is included in width */
          min-width: 0; /* Prevent overflow */
        }

        .path-input:focus {
            outline: none;
            border-color: var(--primary-light, #63b3ed);
            box-shadow: 0 0 0 2px rgba(99, 179, 237, 0.3);
        }
        
        .path-suggestions {
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          max-height: 200px;
          overflow-y: auto;
          background: var(--bg-light, #2d3748);
          border: 1px solid var(--border-color, #4a5568);
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          z-index: 10;
          color: var(--text-dark, #e2e8f0);
        }
        
        .suggestion-item {
          padding: 8px 12px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .suggestion-item:hover, .suggestion-item.highlighted {
          background-color: var(--bg-darker, #1a202c);
        }
        
        .suggestion-path {
          font-weight: bold;
        }
        
        .suggestion-description {
          font-size: 12px;
          color: var(--text-secondary, #a0aec0);
          margin-top: 2px;
          padding: 2px 0;
        }
        
        .description-icon {
          margin-right: 4px;
          display: inline-flex;
          align-items: center;
          vertical-align: middle;
        }
        
        .missing-description {
          color: var(--text-secondary, #a0aec0);
          font-style: italic;
        }
        
        .no-results {
          color: var(--text-secondary, #a0aec0);
          font-style: italic;
          padding: 8px 12px;
        }
        
        .suggestion-metadata {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin: 4px 0;
        }
        
        .attribute-tag {
          display: inline-block;
          padding: 2px 6px;
          border-radius: 3px;
          color: white;
          font-size: 10px;
          font-weight: 500;
        }
        
        .type-tag {
          background: var(--primary-dark, #2b6cb0);
          font-weight: bold;
          border-radius: 3px;
        }
        
        .constraint-tag {
          background: var(--accent-dark, #dd6b20);
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};